import React, { useState, useEffect } from 'react';
import creditMemoService from '../../services/api/creditMemoService';

/**
 * CreateCreditMemoModal Component
 *
 * Modal for creating a new credit memo.
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal closes
 * @param {function} onSuccess - Callback when credit memo is created successfully
 * @param {number} customerId - Required customer ID for the credit memo
 */
const CreateCreditMemoModal = ({ isOpen, onClose, onSuccess, customerId }) => {
  const [formData, setFormData] = useState({
    amount: '',
    reason: '',
    reasonCode: '',
    customerNotes: '',
    internalNotes: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  const reasonCodes = [
    { value: 'overpayment', label: 'Overpayment' },
    { value: 'return', label: 'Return/Refund' },
    { value: 'price_adjustment', label: 'Price Adjustment' },
    { value: 'service_credit', label: 'Service Credit' },
    { value: 'promotional', label: 'Promotional Credit' },
    { value: 'billing_error', label: 'Billing Error' },
    { value: 'goodwill', label: 'Goodwill Adjustment' },
    { value: 'other', label: 'Other' }
  ];

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        amount: '',
        reason: '',
        reasonCode: '',
        customerNotes: '',
        internalNotes: ''
      });
      setError('');
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!customerId) {
      setError('Customer ID is required');
      return;
    }

    const amountCents = Math.round(parseFloat(formData.amount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!formData.reason.trim()) {
      setError('Please enter a reason for the credit');
      return;
    }

    try {
      setIsProcessing(true);

      const result = await creditMemoService.createCreditMemo({
        customerId,
        amountCents,
        reason: formData.reason.trim(),
        reasonCode: formData.reasonCode || null,
        sourceType: 'manual',
        customerNotes: formData.customerNotes.trim() || null,
        internalNotes: formData.internalNotes.trim() || null
      });

      if (result.success) {
        onSuccess && onSuccess(result);
        onClose();
      } else {
        setError(result.error || 'Failed to create credit');
      }
    } catch (err) {
      console.error('Error creating credit:', err);
      setError(err.response?.data?.error || 'Failed to create credit');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-lg bg-white">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Create Credit</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isProcessing}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Amount */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Credit Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                step="0.01"
                min="0.01"
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          {/* Reason Code */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason Code
            </label>
            <select
              name="reasonCode"
              value={formData.reasonCode}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a reason code</option>
              {reasonCodes.map(code => (
                <option key={code.value} value={code.value}>
                  {code.label}
                </option>
              ))}
            </select>
          </div>

          {/* Reason */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              rows={2}
              placeholder="Describe the reason for this credit..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Customer Notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Notes
              <span className="text-gray-400 font-normal ml-1">(visible to customer)</span>
            </label>
            <textarea
              name="customerNotes"
              value={formData.customerNotes}
              onChange={handleChange}
              rows={2}
              placeholder="Optional notes visible to the customer..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Internal Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Internal Notes
              <span className="text-gray-400 font-normal ml-1">(not visible to customer)</span>
            </label>
            <textarea
              name="internalNotes"
              value={formData.internalNotes}
              onChange={handleChange}
              rows={2}
              placeholder="Optional internal notes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={isProcessing}
            >
              {isProcessing ? 'Creating...' : 'Create Credit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCreditMemoModal;
